import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CreditCard, Loader2, ExternalLink, AlertCircle, Sparkles, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Layout } from "../components/Layout";
import AddAddonDialog from "../components/AddAddonDialog";

function fmtMoney(n) {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
}

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

const STATUS_BADGE = {
  active: "bg-[#E6F0EB] text-[#34745C]",
  trialing: "bg-[#FFF4E5] text-[#B07020]",
  past_due: "bg-[#FCE8E4] text-[#B54D40]",
  canceled: "bg-[#F0EDE8] text-[#57605A]",
};

export default function BillingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [opening, setOpening] = useState(false);
  const [availableAddons, setAvailableAddons] = useState([]);
  const [showAddon, setShowAddon] = useState(null);

  const loadSub = () => api.get("/billing/subscription").then((r) => setSub(r.data));
  const loadAddons = () => api.get("/billing/plans").then((r) => {
    const addons = (r.data.plans || []).filter((p) => p.kind === "addon" && !p.coming_soon);
    setAvailableAddons(addons);
  }).catch(() => setAvailableAddons([]));

  useEffect(() => {
    Promise.all([loadSub(), loadAddons()])
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  async function openPortal() {
    setOpening(true);
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setOpening(false);
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 text-[#2E5C50] animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="billing-page" className="max-w-3xl mx-auto space-y-8">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Billing</p>
          <h1 className="mt-2 font-heading text-3xl tracking-tight text-[#1C211F]">Your subscription</h1>
        </div>

        {!sub?.active ? (
          <div data-testid="no-subscription-card" className="bg-white border border-[#E8E6E1] rounded-xl p-8 text-center">
            <div className="w-12 h-12 bg-[#D6E0D9]/50 rounded-full flex items-center justify-center mx-auto">
              <Sparkles className="w-6 h-6 text-[#2E5C50]" strokeWidth={1.5} />
            </div>
            <h2 className="mt-4 font-heading font-semibold text-xl text-[#1C211F]">No active subscription</h2>
            <p className="mt-2 text-sm text-[#57605A] max-w-md mx-auto">
              You are currently in demo mode. Subscribe to unlock the full Nexari platform — document AI,
              compliance verification, EVV, scheduling and more.
            </p>
            {sub?.trial_days > 0 && (
              <p data-testid="trial-cta" className="mt-3 text-sm font-medium text-[#2E5C50]">
                ✨ Start with a {sub.trial_days}-day free trial — no charge until day {sub.trial_days + 1}.
              </p>
            )}
            <Link
              to="/pricing"
              data-testid="see-plans-btn"
              className="mt-6 inline-flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium"
            >
              {sub?.trial_days > 0 ? "Start free trial" : "See plans"}
            </Link>
          </div>
        ) : (
          <>
            <div data-testid="active-subscription-card" className="bg-white border border-[#E8E6E1] rounded-xl p-6">
              {sub.status === "trialing" && sub.trial_end && (
                <div data-testid="trial-countdown" className="mb-4 inline-flex items-center gap-2 bg-[#E6F0EB] text-[#1C211F] border border-[#34745C]/20 px-3 py-1.5 rounded-full text-xs font-medium">
                  <Sparkles className="w-3 h-3 text-[#2E5C50]" strokeWidth={2} />
                  Free trial — {daysUntil(sub.trial_end)} day{daysUntil(sub.trial_end) === 1 ? "" : "s"} left · first charge {fmtDate(sub.trial_end)}
                </div>
              )}
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <span
                    data-testid="subscription-status-badge"
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide ${STATUS_BADGE[sub.status] || "bg-[#F0EDE8] text-[#57605A]"}`}
                  >
                    {sub.status}
                  </span>
                  <h2 className="mt-3 font-heading text-2xl text-[#1C211F]">
                    {fmtMoney(sub.total_monthly)}<span className="text-sm text-[#57605A]">/mo</span>
                  </h2>
              <p className="mt-1 text-sm text-[#57605A]">
                {sub.status === "trialing" ? "First charge on " : (sub.status === "active" ? "Renews on " : "Period ends on ")}
                {fmtDate(sub.status === "trialing" ? sub.trial_end : sub.current_period_end)}
              </p>
                </div>
                <button
                  data-testid="manage-billing-btn"
                  onClick={openPortal}
                  disabled={opening}
                  className="flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium disabled:opacity-60"
                >
                  {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Manage billing
                  <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                </button>
              </div>
            </div>

            <div className="bg-white border border-[#E8E6E1] rounded-xl p-6">
              <h3 className="font-heading font-semibold text-base text-[#1C211F]">What is included</h3>
              <ul className="mt-4 space-y-3">
                {sub.items.map((it) => (
                  <li key={it.stripe_item_id} data-testid={`sub-item-${it.stripe_item_id}`} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#2E5C50]" strokeWidth={2} />
                      <span className="text-[#1C211F]">{it.name}</span>
                    </div>
                    <span className="text-[#57605A]">{fmtMoney(it.amount)}/{it.interval}</span>
                  </li>
                ))}
              </ul>
            </div>

            {(() => {
              const subItemNames = new Set((sub.items || []).map((i) => i.name));
              const upsellable = availableAddons.filter((a) => !Array.from(subItemNames).some((n) => n && n.includes(a.name)));
              if (upsellable.length === 0) return null;
              return (
                <div data-testid="available-addons-card" className="bg-white border border-[#E8E6E1] rounded-xl p-6">
                  <h3 className="font-heading font-semibold text-base text-[#1C211F]">Upgrade your plan</h3>
                  <p className="text-sm text-[#57605A] mt-1">
                    Add a module to your subscription — pro-rated for this billing cycle.
                  </p>
                  <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    {upsellable.map((a) => (
                      <div
                        key={a.id}
                        data-testid={`upsell-addon-${a.id}`}
                        className="border border-[#E8E6E1] rounded-md p-4 flex flex-col"
                      >
                        <h4 className="font-heading font-semibold text-sm text-[#1C211F]">{a.name}</h4>
                        <p className="mt-1 text-xs text-[#57605A] min-h-[32px]">{a.description}</p>
                        <div className="mt-2 mb-3 flex items-baseline gap-1">
                          <span className="text-lg font-semibold text-[#1C211F]">{fmtMoney(a.amount)}</span>
                          <span className="text-xs text-[#57605A]">/mo</span>
                        </div>
                        <button
                          data-testid={`add-addon-${a.id}-btn`}
                          onClick={() => setShowAddon(a)}
                          className="mt-auto flex items-center justify-center gap-1.5 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md py-2 text-xs font-medium"
                        >
                          <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Add to plan
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="bg-[#FFF4E5]/40 border border-[#F0DCB8] rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#B07020] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-xs text-[#57605A]">
                Need to change plans, update card, view invoices or cancel?
                Click <strong className="text-[#1C211F]">Manage billing</strong> to open the secure Stripe customer portal.
              </p>
            </div>

            <div className="text-center">
              <button
                onClick={() => navigate("/pricing")}
                data-testid="see-other-plans-link"
                className="text-sm text-[#2E5C50] hover:underline font-medium"
              >
                See other plans
              </button>
            </div>
          </>
        )}
        <AddAddonDialog
          addon={showAddon}
          open={!!showAddon}
          onClose={() => setShowAddon(null)}
          onAdded={() => { loadSub(); }}
        />
      </div>
    </Layout>
  );
}
